import { inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay } from 'rxjs';

import { API_ROUTES } from '../consts/api-routes.const';
import { Vehicle } from '../pages/appointment/plan-appointment/license-plate-selection/interfaces/vehicle.interface';
import { HttpService } from './http.service';

const LICENSE_PLATE_VALIDATION = /^[A-Z0-9]{1,8}$/;

@Injectable({ providedIn: 'root' })
export class LicensePlateService {
  private readonly httpClient: HttpService = inject(HttpService);

  private readonly vehicleInformationCache = new Map<string, Vehicle | null>();
  private readonly pendingVehicleRequests = new Map<string, Observable<Vehicle | null>>();
  public readonly vehicle = signal<Vehicle | null>(null);

  public readonly currentLicensePlate = signal<string | null>(null);

  public clear(): void {
    this.vehicle.set(null);
    this.vehicleInformationCache.clear();
    this.currentLicensePlate.set(null);
  }

  public setCurrentLicensePlate(rawValue: string | null | undefined): void {
    if (!rawValue) {
      this.currentLicensePlate.set(null);
      return;
    }
    const normalized: string = this.normalizeLicensePlate(rawValue);
    this.currentLicensePlate.set(LICENSE_PLATE_VALIDATION.test(normalized) ? normalized : null);
  }

  public getVehicle(licensePlate: string): Observable<Vehicle | null> {
    const normalizedLicensePlate = this.normalizeLicensePlate(licensePlate);

    if (!normalizedLicensePlate) {
      return of(null);
    }

    if (this.vehicleInformationCache.has(normalizedLicensePlate)) {
      this.vehicle.set(this.vehicleInformationCache.get(normalizedLicensePlate)!);
      return of(this.vehicleInformationCache.get(normalizedLicensePlate)!);
    }

    const pending: Observable<Vehicle | null> | undefined =
      this.pendingVehicleRequests.get(normalizedLicensePlate);
    if (pending) {
      return pending;
    }

    const request: Observable<Vehicle | null> = this.httpClient
      .get<Vehicle>(API_ROUTES.VEHICLE.info(normalizedLicensePlate))
      .pipe(
        map((vehicle: Vehicle | null): Vehicle | null => {
          if (!vehicle) {
            this.vehicleInformationCache.set(normalizedLicensePlate, null);
            return null;
          }

          const result = {
            ...vehicle,
          };

          this.vehicleInformationCache.set(normalizedLicensePlate, result);
          this.vehicle.set(result);

          return result;
        }),
        catchError((): Observable<null> => {
          this.vehicleInformationCache.set(normalizedLicensePlate, null);
          return of(null);
        }),
        finalize((): void => {
          this.pendingVehicleRequests.delete(normalizedLicensePlate);
        }),
        shareReplay(1),
      );
    this.pendingVehicleRequests.set(normalizedLicensePlate, request);
    return request;
  }

  public normalizeLicensePlate(licensePlate: string): string {
    return licensePlate.replaceAll('-', '').trim().toUpperCase();
  }
}
